import { dehydratedAround, ENTER_KEYS, fiberFromNode, handlerOf, hydratedSince, namingFiber, nextDevToolsRoot, ownersOf, profileModeBit, reportsPassiveEffects, rootShapeProblem, strictModeBit, walkCommit, type FiberRoot } from './fiber.js';
import { controlOf } from './element.js';
import { shared } from './session.js';
import type { CommitSummary, HookInfo, HydrationBoundary, InstallOptions, InteractionReport, RendererInfo, Stats, UnsupportedReason } from './types.js';
import { NEWEST_REACT_MAJOR, OLDEST_REACT_MAJOR, parseReactVersion } from './version.js';
import { dropped, errorText, guarded, warnOnce } from './warn.js';

const HOOK_KEY = '__REACT_DEVTOOLS_GLOBAL_HOOK__';
const MAX_COMMITS = 300;
/**
 * `inputWindow` by default, ms. The hook measures it from the end of the input's own work; a report
 * takes later renders within the same length of its paint, or of that end where it came after the
 * paint (`followUpFrom` in join.ts).
 */
export const DEFAULT_INPUT_WINDOW = 1500;

/** Where React and every devtool look for the hook: `window`. */
interface HookHolder {
  [HOOK_KEY]?: unknown;
}

/** A `__REACT_DEVTOOLS_GLOBAL_HOOK__`, reduced to what this library reads, calls or wraps. */
interface DevtoolsHook {
  /** What each renderer handed `inject()`, by id. React DevTools' hook fills it; Fast Refresh's stub does not. */
  renderers?: Map<number, unknown>;
  /** React registers with no hook that sets it: how a page turns React's developer tools support off. */
  isDisabled?: boolean;
  /** React registers only with a hook that sets it. */
  supportsFiber?: boolean;
  inject(internals: unknown): number;
  onCommitFiberRoot(id: number, root: FiberRoot, priority?: number, didError?: boolean): void;
  /** React 18 and 19 call it once a commit's passive effects have run, when the hook has it. */
  onPostCommitFiberRoot?(id: number, root: FiberRoot): void;
  /** Marks the hook this library created. */
  reactInpBlame?: true;
}

/** Why a renderer's commits cannot be read: the kind `stats().unsupportedReason` reports, and the warning's sentence. */
interface Problem {
  kind: Extract<UnsupportedReason['kind'], 'react-version' | 'fiber-shape' | 'walk-threw'>;
  message: string;
}

interface Renderer {
  info: RendererInfo;
  /** Only react-dom commits are walked: other renderers have no DOM behind their fibers. */
  isReactDom: boolean;
  /** Its version is an experimental build's, read as the newest React (see `parseReactVersion`). */
  experimental: boolean;
  /** Its React major, 0 outside the versions read. */
  major: number;
  /**
   * React passes the Scheduler priority of the lanes a commit rendered, which tells continuous-event work
   * apart: React 19.1 and later. 19.0 and 18 pass the priority of the moment the commit is made.
   */
  lanePriority: boolean;
  /** `profileModeBit` for its React major. */
  profileMode: number;
  /** `strictModeBit` for its React major, or 0 outside a development build, the only one that renders twice. */
  strictMode: number;
  /**
   * Whether a root of the app it has committed is in ProfileMode, which in a build that is not a development
   * one only a profiling build sets. Null until its first commit of the app's.
   */
  profiled: boolean | null;
  /** Why its commits cannot be read (a React outside 17 to 19, a root of another shape, a walk that threw), or null. */
  problem: Problem | null;
  /** Its first commit has been checked. */
  checked: boolean;
  /** Every root it has committed so far is Next.js's dev overlay's, and those are never read (see `nextDevToolsRoot`). */
  devToolsOnly: boolean;
}

/** The fields of a pointer or key event the ring reads. */
interface DispatchedInput {
  readonly isTrusted: boolean;
  readonly type: string;
  readonly timeStamp: number;
  readonly target: EventTarget | null;
  readonly pointerId?: number;
  readonly pointerType?: string;
  readonly code?: string;
  readonly keyCode?: number;
}

/** An input stamped on a commit: the event being dispatched when it ran, else the newest one seen. */
export interface InputStamp {
  /** `Event.timeStamp`, the same clock as Event Timing's `startTime`. */
  readonly ts: number;
  readonly type: string;
  /** `timeStamp` of the pointerdown or keydown that began the press this input is part of; equals `ts` for those. */
  readonly gestureTs: number;
}

/** One input the library saw at dispatch. The last 8 are kept in a ring. */
export interface InputRecord extends InputStamp {
  /** `pointerId` for pointer events, `code` for key events: how a pointerup or keyup finds its press. */
  readonly press: string | number | undefined;
  /** 'mouse', 'pen' or 'touch' for a pointer event; empty or absent for a key, and for a click a key made. */
  readonly pointerType?: string;
  readonly target: Node | null;
  /**
   * The control the target is inside (the button around a clicked icon), found at dispatch while both
   * are still in the page; the target itself when there is none. Absent on records made elsewhere.
   */
  readonly control?: Node | null;
  /**
   * The report's label for the control, read at dispatch: a click on "Count is 0" that renders "Count
   * is 1" is labelled by what was clicked. Absent on records made elsewhere and before install().
   */
  readonly label?: string | null;
  /**
   * The components enclosing the target at dispatch (the control, for a click on an icon inside one), nearest first. Read before React's handlers run:
   * once React commits the deletion of an element, React 18 and 19 clear its fiber's links and props, so
   * a clicked row that deleted itself is still named after what it was.
   */
  readonly owners: readonly string[];
  /**
   * The React handler prop for this input's type on the target chain at dispatch, read then for the same reason.
   * On server HTML there is none to read then, and it is read again once React has hydrated the target to run the
   * input, before any handler runs (`hydratedRead`).
   */
  handler: string | null;
  /**
   * The key `handler` was read for, which decides whether it can reach an onSubmit: a key event's `code`,
   * or null for one an input method took (`IME_KEY_CODE`) and for any other input. An element read later
   * for this event is read for the same key.
   */
  readonly key: string | null;
  /**
   * For a keydown, the handler prop its keypress reached, read as the keypress was dispatched
   * (`noteKeypress`), since a keypress has no record of its own. Undefined until one comes, for a key that
   * fires none, and for a keypress on server HTML React had not hydrated.
   */
  keypressHandler?: string | null;
  /**
   * For an Enter's keydown, the onSubmit its keypress reached, read with `keypressHandler` and apart from it: a
   * field's own onKeyPress comes first there, and does none of the work of the click Enter makes on the form's
   * submit button (`submittedBy` in join.ts). Undefined where `keypressHandler` is, and for any other key.
   */
  keypressSubmit?: string | null;
  /**
   * Server-rendered HTML enclosing the target that React had not hydrated when the input was
   * dispatched; null when React had hydrated it, and on a page with no React root above the target.
   */
  readonly dehydrated: HydrationBoundary | null;
  /**
   * Whether `handler` was read again at the commit that hydrated `dehydrated`, which React makes inside the input's
   * own dispatch, to run it, before it runs a handler (`onCommit`). Undefined for an input on HTML React had
   * hydrated before, and for one React did not hydrate inside its dispatch.
   */
  hydratedRead?: boolean;
  /** What React did about this input. The hook keeps it current as commits arrive; the rest of the record is read at dispatch, the keypress's readings at the keypress's and `handler` on server HTML at the commit that hydrated it, and does not change. */
  readonly work: InputWork;
}

/** The React work one input caused, as the DevTools hook saw it. */
export interface InputWork {
  /**
   * Where the window for joining later commits is measured from: the input's `ts` until React commits
   * inside its dispatch, then the end of the last such commit. A commit that arrives after the dispatch
   * is joined to the input while it lands within `inputWindow` of this, so a slow interaction's
   * follow-up render is still its own rather than cut off a fixed time after the input.
   *
   * It is the end of the last commit inside the dispatch, not the end of the dispatch. A handler that
   * runs for two seconds and commits nothing leaves this on the input itself, and a render it starts
   * afterwards can fall outside the window and be dropped. Dropped is not lost: the time is kept in
   * `unjoined` and the report says React rendered something it could not tie to the interaction.
   */
  endedAt: number;
  /**
   * `endedAt` as the input's own task left it. A commit inside a derived event from a later task, a
   * `change` or an `input`, moves `endedAt` and not this, and such an event counts as the input's
   * dispatch only within `inputWindow` of this, so a stream of them (dictation, an input method,
   * autofill) cannot hold the window open by carrying it forward one event at a time.
   */
  ownEndedAt: number;
  /**
   * When commits React made while this was the newest input landed, for the ones not joined to it
   * because they came past that window, newest last and capped. A report counts only those that ran
   * inside one of its interaction's processing spans; on a page with a clock in it, most are the clock.
   * A report with any says so and is never `measured`: React did render, and this library cannot say
   * what the render belonged to.
   */
  unjoined: number[];
  /**
   * The times of the first few `input`, `change` and `submit` events a script dispatched while this was
   * the newest input, once its task had ended, and outside the task of a trusted one of those handed to
   * it. None of them is an interaction, but a render after one can be its work rather than this input's,
   * so a report takes no later render past one that came after its paint (`noteCloser`). Absent until one
   * arrives.
   */
  closers?: number[];
}

export interface HookOptions {
  hook: NonNullable<InstallOptions['hook']>;
  walkBudget: number;
  inputWindow: number;
  onSummary: (c: CommitSummary) => void;
  /** Names the control around the node an input landed on, at dispatch (see `InputRecord.label`). */
  label?: (target: Node) => string | null;
}

/**
 * The React work the page's report listeners caused on one root. A listener that shows reports renders
 * when it hears one. That render has no input of its own, so it would be stamped with the report's
 * input, join the report as its later render, and reach the listener again as the report's next
 * revision: a loop on any page that renders its reports. React sets a lane (a bit) in
 * `root.pendingLanes` for each update and clears it when the update commits, so the lanes the listeners
 * leave pending name their work, and so do the lanes that work schedules in turn.
 */
interface ListenerWork {
  /** Pending lanes whose work the listeners caused. */
  lanes: number;
  /** `pendingLanes` when last looked at: a lane set since is new. */
  seen: number;
  /** The root's last commit was the listeners' work, and React has not yet said that its passive effects ran. */
  effectsPending: boolean;
}

interface HookState {
  options: HookOptions | null;
  /** The hook commits are read from while installed. */
  attached: DevtoolsHook | null;
  /**
   * The hook in use when the page turned it off after install() (`checkHookReplaced`), which `debug.hook()` goes on
   * describing, with the renderers that registered with it. Absent where a copy of an earlier version made the state.
   */
  turnedOffHook?: DevtoolsHook | null;
  /** Puts a chained hook back the way it was. */
  detach: (() => void) | null;
  /** The hook this library created. React keeps the hook it registered with for the page's life, so a second install reuses it. */
  shim: DevtoolsHook | null;
  devtoolsLockedOut: boolean;
  /** How the hook is in use: 'shim', 'chained' or 'none', or 'unsupported' when the page has disabled or locked the hook. */
  mode: Stats['mode'];
  /** Why the page's hook cannot be used at all. A renderer's own problem is on the renderer. */
  unsupported: UnsupportedReason | null;
  /** Every commit walked so far, oldest first. */
  commits: CommitSummary[];
  walks: number;
  walkTotalMs: number;
  /** Renderers per hook object, by the id that hook's inject() returned. */
  registries: WeakMap<DevtoolsHook, Map<number, Renderer>>;
  /**
   * Per hook, what its `onCommitFiberRoot` and `onPostCommitFiberRoot` read as when the library last took it up,
   * wrapped or not, or let it go on dispose() (`turnedOff`). Absent where a copy of an earlier version made the state.
   */
  commitMethods?: WeakMap<DevtoolsHook, Pick<DevtoolsHook, 'onCommitFiberRoot' | 'onPostCommitFiberRoot'>>;
  /** The last 8 inputs seen, oldest first. */
  inputs: InputRecord[];
  /** The newest input while the task that dispatched it is still running; null once a task queued behind it has run. */
  inTask: InputRecord | null;
  /**
   * The newest input while a later task runs a trusted `input`, `change` or `submit` that
   * `dispatchedInput` hands to it (`noteCloser`); null once a task queued behind that one has run.
   * Absent where a copy of an earlier version made the state.
   */
  derivedTask?: InputRecord | null;
  /**
   * `timeStamp` of the last `resize` that changed the page's width (`noteResize`), and that width. Absent
   * where a copy of an earlier version made the state; `noteResize` fills them in.
   */
  resizedAt?: number;
  width?: number;
  /** Every root that committed while installed, held weakly so that an unmounted root is not kept alive by this list. */
  roots: WeakRef<FiberRoot>[];
  /**
   * The length at which `roots` next lets go of the roots React has dropped (`listenerWorkOf`). Absent until
   * the list first reaches `MIN_PRUNE_AT`, where a copy of an earlier version made the state, and after dispose().
   */
  pruneAt?: number;
  /** What the page's report listeners caused on each root in `roots`. */
  listenerWork: WeakMap<FiberRoot, ListenerWork>;
  /** The page's report listeners are running. */
  hearing: boolean;
  /** Per root, the commits React has not yet said ran their passive effects, newest last (`onPostCommit`). */
  awaitingEffects: WeakMap<FiberRoot, AwaitingEffects[]>;
}

/** A commit's place until React says its passive effects ran. */
interface AwaitingEffects {
  /** The commit as walked, or null for one that was not. */
  summary: CommitSummary | null;
  /** Its tree holds passive work, so React will say when that ran (`reportsPassiveEffects`). */
  reported: boolean;
  /**
   * When the hook call for it returned, every tool chained on the hook included (React DevTools reads
   * each commit too). The effects start after that, so nothing before it is theirs.
   */
  returnedAt: number;
}

/** One for the page, whichever copy of the library installed (see session.ts). */
const state = shared<HookState>('hook', () => ({
  options: null,
  attached: null,
  detach: null,
  shim: null,
  devtoolsLockedOut: false,
  mode: 'none',
  unsupported: null,
  commits: [],
  walks: 0,
  walkTotalMs: 0,
  registries: new WeakMap(),
  inputs: [],
  inTask: null,
  roots: [],
  listenerWork: new WeakMap(),
  hearing: false,
  awaitingEffects: new WeakMap(),
}));

// The events Event Timing gives an interactionId to, and so the only ones an interaction is ever
// named by. A derived event (`DERIVED_TYPES` below) is dispatched inside one of these, so a commit
// during one is stamped with the newest ring entry, which is the key or pointer that caused it.
export const INPUT_TYPES = ['pointerdown', 'pointerup', 'click', 'keydown', 'keyup'];
const DERIVED_TYPES = ['input', 'beforeinput', 'change', 'submit', 'keypress'];
// Events that are never an interaction's work: the page resized or scrolled, or the pointer moved over it.
// A commit React makes while one of them is being dispatched, outside any input's task, is that event's:
// a handler under React 17, which renders inside every event, or a flushSync in a scroll listener. React 18
// and 19 render what a `resize` or a hover sets in a task of their own, which `noteResize` and the priority
// below tell apart.
const AMBIENT_TYPES = [
  'resize',
  'scroll',
  'scrollend',
  'wheel',
  'visibilitychange',
  'pointermove',
  'pointerover',
  'pointerout',
  'pointerenter',
  'pointerleave',
  'mousemove',
  'mouseover',
  'mouseout',
  'mouseenter',
  'mouseleave',
  'touchmove',
];
// The Scheduler priority React 19.1 and later pass with a commit of continuous-event work: a hover, a
// scroll, a wheel or a drag, rendered in a task of its own after the event. They derive it from the lanes
// they rendered; React 18 and 19.0 pass the priority of the moment they commit, which in that task is normal.
const USER_BLOCKING_PRIORITY = 2;
const RING_SIZE = 8;
// Times of commits that could not be joined to one input, kept per input. A page that commits in a
// loop would otherwise grow this without end; the oldest are the least likely to be worth reporting.
const MAX_UNJOINED = 16;
/** Commits per root waiting for React to say their passive effects ran. Only one committed inside another's effects waits behind a newer one. */
const MAX_AWAITING_EFFECTS = 8;
// The fewest roots held before the ones React has dropped are let go of (`listenerWorkOf`).
const MIN_PRUNE_AT = 64;
// A press can be held this long and its release still counts as the same gesture.
const PRESS_WINDOW = 5000;
// The keyCode browsers give a keydown an input method took, and React's own test for one. Its Enter
// commits the text and submits nothing, and the field's onChange runs from the input event that ends the
// composition, so the handler is read as for a key that is not Enter.
const IME_KEY_CODE = 229;

/** The last 8 inputs seen, oldest first. Live array, do not mutate. */
export function recentInputs(): InputRecord[] {
  return state.inputs;
}

/** Capture-phase listener for INPUT_TYPES: keeps the ring current. */
export function noteInput(e: Event): void {
  if (!e.isTrusted || INPUT_TYPES.indexOf(e.type) < 0) return;
  record(e);
}

/**
 * Capture-phase listener for `keypress`: reads the handler it reaches onto its keydown's record, before
 * React runs it. Enter in a field submits the form from the keypress, so its entry is where Chromium puts
 * the submit's work, and the submit's own render can give the form another onSubmit before that entry
 * comes: `onSubmit={step < 2 ? goNext : finish}` does on the first step. Read when the entry came, the
 * form named the step after. Server HTML React has not hydrated has no handler to read yet, and the form
 * is read when the entry comes. HTML React hydrated inside the keydown, to run it, is read now. For Enter
 * the onSubmit is read on its own as well, since a field's onKeyPress comes before it in what the keypress
 * reaches.
 */
export function noteKeypress(e: Event): void {
  const last = newestInput();
  const code = (e as DispatchedInput).code;
  if (!e.isTrusted || !last || last.type !== 'keydown' || last.press !== code || last.keypressHandler !== undefined) return;
  const target = e.target as Node | null;
  if (dehydratedAround(target) !== null) return;
  const fiber = fiberFromNode(target);
  last.keypressHandler = handlerOf(fiber, 'keypress', code);
  if (code && ENTER_KEYS.includes(code)) last.keypressSubmit = handlerOf(fiber, 'submit');
}

/** The events that close the newest input's later renders when a script dispatches one (`noteCloser`). */
export const CLOSER_TYPES = ['input', 'change', 'submit'];

/**
 * Capture-phase listener for CLOSER_TYPES. One a script dispatched outside any input's task has no Event
 * Timing entry and no place in the ring, yet it is something new happening on the page, and a render
 * after it can be its work rather than the newest input's. Sorting a table by a click and then changing
 * its page size through Playwright's `selectOption`, which fires `input` and `change` from script, put
 * the page-size render on the sort as its later render. The time is kept on the newest input
 * (`InputWork.closers`), where `join.ts` reads it. One the browser fires is left alone: it comes in the
 * task of the key or click that caused it, or carries on what that input began (an option picked from
 * the select it opened, or text dictated into the field it focused), which `dispatchedInput` hands to
 * the input within `inputWindow`. So is one a script dispatches in the task of such a trusted event, as a
 * select's onChange firing `input` on another field does: it answers the same input (`derivedTask`).
 */
export function noteCloser(e: Event): void {
  const last = newestInput();
  if (state.inTask !== null || !last) return;
  if (e.isTrusted) {
    if (isNode(e.target) && e.timeStamp - last.work.ownEndedAt <= (state.options?.inputWindow ?? DEFAULT_INPUT_WINDOW)) {
      state.derivedTask = last;
      setTimeout(() => {
        if (state.derivedTask === last) state.derivedTask = null;
      }, 0);
    }
    return;
  }
  if (state.derivedTask === last) return;
  // The first few are enough: only the first after the report's paint closes anything, and at most a
  // handful come between the end of the input's task and its paint.
  const closers = (last.work.closers ??= []);
  if (closers.length < MAX_UNJOINED) closers.push(e.timeStamp);
}

function record(e: DispatchedInput): InputRecord {
  const isKey = e.type === 'keydown' || e.type === 'keyup';
  const target = e.target as Node | null;
  let read: TargetReading;
  try {
    read = readTarget(e, target, isKey);
  } catch (error) {
    // A target the library cannot read, such as a field of a form whose fields shadow its properties, still
    // records its input, read as one with no target: the commits in its dispatch are its own, and it names
    // nothing.
    dropped(error);
    read = readTarget(e, null, isKey);
  }
  const rec: InputRecord = {
    ts: e.timeStamp,
    type: e.type,
    gestureTs: gestureOf(e, isKey),
    press: isKey ? e.code : e.pointerId,
    pointerType: e.pointerType,
    target,
    ...read,
    work: { endedAt: e.timeStamp, ownEndedAt: e.timeStamp, unjoined: [] },
  };
  state.inputs.push(rec);
  if (state.inputs.length > RING_SIZE) state.inputs.shift();
  // A task queued now runs only after the one dispatching this input has finished, with whatever
  // derived events the browser fires from it.
  state.inTask = rec;
  setTimeout(() => {
    if (state.inTask === rec) state.inTask = null;
  }, 0);
  return rec;
}

/** What an input's target says at dispatch. */
type TargetReading = Pick<InputRecord, 'control' | 'label' | 'owners' | 'handler' | 'key' | 'dehydrated'>;

function readTarget(e: DispatchedInput, target: Node | null, isKey: boolean): TargetReading {
  // Read now, before React's handlers run: once React commits the deletion of the element, React 18
  // and 19 clear its fiber's links and props, and the Event Timing entry arrives after that.
  const fiber = fiberFromNode(target);
  // The control labels the click, and for a click on an icon the components are read from what the icon
  // belongs to: an icon library's `Trash2` inside the button is not what anyone clicked. Both found now,
  // since a click that swaps the icon detaches it before the entry.
  const control = controlOf(target);
  const key = (isKey && e.keyCode !== IME_KEY_CODE && e.code) || null;
  return {
    control,
    // Asked of the target, as a report asks of its entry's: the control around it is the same, and whether
    // the input landed inside an editor is asked of the element it landed on.
    label: target && state.options?.label?.(target),
    owners: Object.freeze(ownersOf(namingFiber(target))),
    handler: handlerOf(fiber, e.type, key),
    key,
    // Asked of every input, not only of one with no fiber: a Suspense boundary can still be waiting
    // inside a page React has otherwise hydrated, and then the target's nearest fiber is the hydrated
    // ancestor above the boundary. React reads the same markers on every event it dispatches.
    dehydrated: frozen(dehydratedAround(target)),
  };
}

const frozen = <T>(x: T | null): T | null => (x === null ? null : Object.freeze(x));

/** Inputs whose wait for server-rendered HTML has already been credited to a commit, so it is credited once. */
const credited = new WeakSet<InputRecord>();

/**
 * The server-rendered HTML this input landed on, if this is the commit that hydrated it. Asked of
 * the DOM, which the commit has already updated, and answered at most once per input: a later commit
 * in the same window finds the page hydrated and is not a second hydration of it.
 */
function creditHydration(input: InputRecord): HydrationBoundary | null {
  if (input.dehydrated == null || credited.has(input)) return null;
  const done = hydratedSince(input.target, input.dehydrated);
  if (done) credited.add(input);
  return done;
}

/**
 * The pointerdown or keydown this event releases, by pointerId or key code; the newest press as a fallback.
 *
 * A click made from the keyboard has no pointer to pair by, and Chrome gives it pointerId -1. It comes in
 * the task of the key that made it, Enter's keydown or a Space's keyup, and is part of that key's press
 * whatever its pointerId says. Paired by the fallback it took the newest pointerdown within PRESS_WINDOW,
 * an earlier mouse click's, and its render joined that click's report. The one click in a key's task that
 * is not the key's is a tap's (`tapInFlight`). Any other click with pointerId -1 takes the press of the
 * input whose task it came in, and with none behind it is a gesture of its own.
 */
function gestureOf(e: DispatchedInput, isKey: boolean): number {
  if (e.type === 'pointerdown' || e.type === 'keydown') return e.timeStamp;
  const task = state.inTask;
  const fromKey = task !== null && (task.type === 'keydown' || task.type === 'keyup');
  if (e.type === 'click' && (fromKey || e.pointerId === -1) && !tapInFlight(e)) return task ? task.gestureTs : e.timeStamp;
  const want = isKey ? 'keydown' : 'pointerdown';
  const press = isKey ? e.code : e.pointerId;
  let fallback = -1;
  for (let i = state.inputs.length - 1; i >= 0; i--) {
    const r = state.inputs[i];
    if (!r || r.type !== want || e.timeStamp - r.ts > PRESS_WINDOW) continue;
    if (press !== undefined && r.press === press) return r.ts;
    if (fallback < 0) fallback = r.ts;
  }
  return fallback < 0 ? e.timeStamp : fallback;
}

/**
 * Whether this is a tap's click whose pointer went down and has had no click since. A tap's click comes in
 * a task of its own after the touchend, and a key pressed just before it can still be `state.inTask` then,
 * since Chromium runs input ahead of the timer the key's task left to clear it. That click is the tap's,
 * and pairs by its pointerId. Only touch and pen go that way: a mouse's click comes in its pointerup's
 * task, and a mouse press that never clicked (a right click, a drag) must not take a keyboard click that
 * carries its pointerId.
 */
function tapInFlight(e: DispatchedInput): boolean {
  if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return false;
  for (let i = state.inputs.length - 1; i >= 0; i--) {
    const r = state.inputs[i];
    if (!r || r.press !== e.pointerId || e.timeStamp - r.ts > PRESS_WINDOW) continue;
    if (r.type === 'click') return false;
    if (r.type === 'pointerdown') return true;
  }
  return false;
}

/**
 * The input being dispatched right now: `window.event`, when it is one of INPUT_TYPES, whose
 * `timeStamp` is exactly its Event Timing entry's `startTime`. Recorded in the ring if the capture
 * listener has not seen it yet. Null outside an input's dispatch.
 *
 * When `window.event` is one of DERIVED_TYPES, such as an `input` or a `change`, this returns the
 * newest input in the ring instead, because the browser fires a derived event as part of the input
 * that caused it. Typing is the case that matters: React's onChange for a text field runs during the
 * native `input` event, not during the keydown, and the keystroke's own render would otherwise look
 * like an unrelated commit. An event a script makes and sends with dispatchEvent() is not trusted,
 * so a `change` or `submit` dispatched that way gets null, the same as no event at all.
 *
 * Only while the derived event is part of that input: fired in the task that dispatched it, or within
 * `inputWindow` of the end of the work that task did (`ownEndedAt`). That is measured from the derived
 * event's own `timeStamp`, since a slow render inside an `input` event ends seconds after it. A
 * `change` the browser fires once a file is chosen in the system dialog, an option is picked from a
 * native select's popup or a password manager fills a field can come long after the click on it, and
 * gets null: its commit is then held to the window like any other, and cannot make a late render the
 * click's own. A derived event from a later task does not move `ownEndedAt`, so text that arrives
 * with no key pressed, from dictation or an input method, stops counting once it passes the window.
 */
export function dispatchedInput(): InputRecord | null {
  const ev = typeof window !== 'undefined' ? (window.event as DispatchedInput | undefined) : undefined;
  if (!ev || !ev.isTrusted) return null;
  if (DERIVED_TYPES.indexOf(ev.type) >= 0) {
    // A `change` from a MediaQueryList, the screen's orientation or the network connection is not part
    // of any input: only an element's (or the document's) derived event is.
    if (!isNode(ev.target)) return null;
    const last = newestInput();
    if (!last) return null;
    return last === state.inTask || ev.timeStamp - last.work.ownEndedAt <= (state.options?.inputWindow ?? DEFAULT_INPUT_WINDOW) ? last : null;
  }
  if (INPUT_TYPES.indexOf(ev.type) < 0) return null;
  const last = newestInput();
  return last && last.ts === ev.timeStamp ? last : record(ev);
}

/**
 * How long after an input's own work a commit can still join it, as this page configured it
 * (`inputWindow`), or null before install() has run. Reports quote it when their explanation is first
 * read, so while the hook is installed a note gives the number the hook actually used rather than the
 * default; a report first read after dispose() quotes the default.
 */
export function joinWindow(): number | null {
  return state.options?.inputWindow ?? null;
}

/** A DOM node, told by its `nodeType`, so that a node of another frame counts too. */
const isNode = (target: EventTarget | null): boolean => target !== null && typeof (target as { nodeType?: unknown }).nodeType === 'number';

/**
 * Whether `window.event` is a trusted event whose commits are its own and no input's: one of
 * AMBIENT_TYPES, a `change` from something that is not an element (a media query hook), or the window
 * itself gaining or losing focus. Asked only outside an input's task, where such an event cannot be
 * the input's own doing.
 */
function inAmbientEvent(): boolean {
  const ev = typeof window !== 'undefined' ? (window.event as DispatchedInput | undefined) : undefined;
  if (!ev || !ev.isTrusted) return false;
  if (AMBIENT_TYPES.indexOf(ev.type) >= 0) return true;
  if (ev.type === 'change') return !isNode(ev.target);
  return (ev.type === 'focus' || ev.type === 'blur') && ev.target === window;
}

/**
 * Capture-phase listener for `resize`: a render after the page changed width is the page's, a layout
 * hook's or a media query's, and not the last input's (`onCommit`). A resize that changes only the
 * height is a phone's keyboard or address bar coming and going, which a tap on a field causes, and the
 * render after it can still be the tap's.
 */
export function noteResize(e: Event): void {
  if (!e.isTrusted) return;
  const width = window.innerWidth;
  if (width === state.width) return;
  state.width = width;
  state.resizedAt = e.timeStamp;
}

/** Whether `window.event` is one of DERIVED_TYPES, which `dispatchedInput` hands to the newest input. */
function inDerivedEvent(): boolean {
  const ev = typeof window !== 'undefined' ? window.event : undefined;
  return ev !== undefined && DERIVED_TYPES.indexOf(ev.type) >= 0;
}

/** The newest input in the ring. */
function newestInput(): InputRecord | null {
  return state.inputs[state.inputs.length - 1] ?? null;
}

/** Every commit walked so far, oldest first. Live array. */
export function recordedCommits(): CommitSummary[] {
  return state.commits;
}

export function clearCommits(): void {
  state.commits.length = 0;
}

/** The hook's half of `stats()`. It only reads. */
export function hookStats(): Pick<Stats, 'mode' | 'unsupportedReason' | 'walks' | 'walkTotalMs'> {
  const unsupportedReason = state.unsupported ?? unreadableReactDom();
  return { mode: unsupportedReason ? 'unsupported' : state.mode, unsupportedReason, walks: state.walks, walkTotalMs: state.walkTotalMs };
}

/** Whether a react-dom the page renders with has registered with the hook in use and can be read. */
export function readingReactDom(): boolean {
  if (!state.attached || (state.mode !== 'shim' && state.mode !== 'chained')) return false;
  for (const renderer of registryOf(state.attached).values()) {
    if (renderer.isReactDom && !renderer.devToolsOnly && !renderer.problem) return true;
  }
  return false;
}

/**
 * What tells React's own listener in a report's scripts. `roots` are the containers of the roots committed so
 * far, as Long Animation Frames names a listener's target ("DIV#root", "#document"), where that names one node:
 * a container with an id, the body, or the document. `named` is every react-dom read being a development build,
 * whose listener is known by its name (`dispatchDiscreteEvent`) rather than by the container it listens on.
 */
export interface ReactPage {
  readonly roots: readonly string[];
  readonly named: boolean;
}

/** The `ReactPage` a report is built with, read as it is built. */
export function reactPage(): ReactPage {
  const roots = new Set<string>();
  for (const ref of state.roots) {
    const container = ref.deref()?.containerInfo as { nodeName?: unknown; id?: unknown } | null | undefined;
    if (typeof container?.nodeName !== 'string') continue;
    const id = typeof container.id === 'string' ? container.id : '';
    if (id || container.nodeName === 'BODY' || container.nodeName === '#document') roots.add(`${container.nodeName}${id ? `#${id}` : ''}`);
  }
  let named = false;
  if (state.attached) {
    for (const renderer of registryOf(state.attached).values()) {
      if (!renderer.isReactDom || renderer.devToolsOnly || renderer.problem) continue;
      if (renderer.info.bundleType !== 1) return { roots: [...roots], named: false };
      named = true;
    }
  }
  return { roots: [...roots], named };
}

/** `api.debug.hook()`. */
export function hookInfo(): HookInfo {
  return { owner: owner(), renderers: knownRenderers(), devtoolsLockedOut: state.devtoolsLockedOut };
}

/**
 * `InteractionReport.reactBuild`: 'development' where any react-dom on the page says so to the DevTools hook,
 * as the badge's mark does, in whatever order they registered, one this library cannot read included, since the
 * page still renders with it. Otherwise what the readable ones' commits showed, a profiling build over a production one.
 */
export function reactBuild(): InteractionReport['reactBuild'] {
  const hook = state.attached ?? state.turnedOffHook;
  if (!hook) return null;
  let build: InteractionReport['reactBuild'] = null;
  for (const r of registryOf(hook).values()) {
    if (!r.isReactDom || r.devToolsOnly) continue;
    if (r.info.bundleType === 1) return 'development';
    if (r.problem) continue;
    if (r.info.bundleType === 0 && r.profiled !== null && build !== 'profiling') build = r.profiled ? 'profiling' : 'production';
  }
  return build;
}

/** What each renderer known to the hook in use, or to the one the page turned off, handed `inject()`. */
export function knownRenderers(): RendererInfo[] {
  const hook = state.attached ?? state.turnedOffHook;
  return hook ? [...registryOf(hook).values()].map((r) => r.info) : [];
}

/** Why no react-dom on the page can be read, when every one registered has a problem; null while one can be, or before any registers. */
function unreadableReactDom(): UnsupportedReason | null {
  if (!state.attached) return null;
  let first: Problem | null = null;
  for (const renderer of registryOf(state.attached).values()) {
    // The dev overlay's own react-dom is never read, so it is not one the page can still be read through.
    if (!renderer.isReactDom || renderer.devToolsOnly) continue;
    if (!renderer.problem) return null;
    first ??= renderer.problem;
  }
  return first && { kind: first.kind, message: first.message };
}

/**
 * Looks for a page that turned the hook in use off where it is, or emptied the global over the shim, and for
 * a tool that replaced the shim by redefining or deleting the global, which its accessor cannot see (an
 * assignment it can), or by assigning over a shim the global holds as a plain value. install() calls it at
 * fixed points, so reading `stats()` or `debug.hook()` never changes what they report.
 */
export function checkHookReplaced(): void {
  const { attached, shim } = state;
  if (!attached) return;
  if (turnedOff(attached)) {
    // The library is the first import, so a script that keeps developer tools out finds the shim, or the hook it
    // chained onto, and turns support off, most with each method made a no-op in place of the library's.
    const message =
      "the page turned its __REACT_DEVTOOLS_GLOBAL_HOOK__ off after install() (isDisabled, or no supportsFiber), so React's commits cannot be read. Interactions are still reported, without components.";
    state.detach?.();
    unusable(message);
    state.turnedOffHook = attached;
    warnOnce('hook-disabled', message);
  } else if (attached === shim) {
    let current: unknown;
    try {
      current = (window as unknown as HookHolder)[HOOK_KEY];
    } catch (error) {
      // A getter a script put there to lock the page down, after install(): the page's doing, not an error of
      // the library's. A React that registered with the shim keeps reporting to it, so there is nothing to say.
      if (!registryOf(shim).size) globalUnusable(error);
      return;
    }
    if (current === shim) return;
    if (Object(current) !== current && !reactDomOn(shim)) {
      // Not an object or function: the page emptied the global (undefined, false, or a delete) before react-dom
      // loaded, which then finds no hook it can register with. Looked for here and not in the accessor, so a tool
      // that assigns a hook of its own right after is still followed.
      const message =
        "the page emptied its __REACT_DEVTOOLS_GLOBAL_HOOK__ after install(), so React registers with no hook and its commits cannot be read. Interactions are still reported, without components.";
      unusable(message);
      warnOnce('hook-disabled', message);
    } else {
      replaced(shim, current);
    }
  }
}

/**
 * Whether the page has turned `hook` off. React reads `isDisabled` and `supportsFiber` only as a react-dom
 * registers, and goes on calling the hook it registered with whatever they say later. So once one has, the page
 * has stopped React's commits only where it has also replaced both methods the library left there, as the scripts
 * that keep developer tools out do. One method that is not the library's says nothing by itself: Fast Refresh wraps
 * the shim's `inject` and `onCommitFiberRoot` when it loads after the library, and passes React's calls on.
 */
function turnedOff(hook: DevtoolsHook): boolean {
  if (!hook.isDisabled && hook.supportsFiber) return false;
  // Until a react-dom registers, the two fields are all React goes by. A global that holds no object has neither
  // field, and nothing registers with it.
  if (!reactDomOn(hook)) return true;
  const left = state.commitMethods?.get(hook);
  // A hook that had no onPostCommitFiberRoot has none left once dispose() took the library's away.
  return !left || (hook.onCommitFiberRoot !== left.onCommitFiberRoot && (typeof left.onPostCommitFiberRoot !== 'function' || hook.onPostCommitFiberRoot !== left.onPostCommitFiberRoot));
}

/** Whether a react-dom has registered with `hook`, which can be a global that holds no object. */
function reactDomOn(hook: DevtoolsHook): boolean {
  for (const renderer of state.registries.get(hook)?.values() ?? []) if (renderer.isReactDom) return true;
  return false;
}

function owner(): string {
  const hook = state.attached ?? state.turnedOffHook;
  if (!hook) return 'none';
  // The page turning the shim off empties its mark too.
  if (hook.reactInpBlame || hook === state.shim) return 'react-inp-blame';
  const keys = Object.keys(hook);
  return keys.length ? `existing hook (${keys.slice(0, 8).join(', ')}${keys.length > 8 ? ', ...' : ''})` : 'existing hook';
}

/**
 * React tells the DevTools hook about every commit, in production builds too, but only if the
 * hook exists before react-dom evaluates. So this chains onto the hook that is already there
 * (React DevTools, Fast Refresh) or, unless told to only chain, creates a minimal one.
 */
export function installHook(opts: HookOptions): void {
  state.options = opts;
  state.width = window.innerWidth;
  state.resizedAt = undefined;
  const holder = window as unknown as HookHolder;
  let existing: DevtoolsHook | undefined;
  try {
    existing = holder[HOOK_KEY] as DevtoolsHook | undefined;
    // attach() first asks a hook other than the shim whether the page turned it off, which reads isDisabled
    // and supportsFiber, and a script can make those throw too.
    if (existing && existing !== state.shim) turnedOff(existing);
  } catch (error) {
    // A getter a script put there to lock the page down, on the global or on the hook it holds: no hook
    // can be reached through it.
    globalUnusable(error);
    return;
  }
  if (existing && existing === state.shim) {
    attach(existing, 'shim');
  } else if (existing) {
    attach(existing, 'chained');
    // Not where the hook turns React's support off or could not be wrapped: those have warnings of their own.
    if (opts.hook === 'shim' && state.mode === 'chained') {
      warnOnce('shim-over-hook', "hook: 'shim' found a React DevTools hook already installed and chained onto it instead: replacing it would lock out whatever installed it.");
    }
  } else if (opts.hook === 'chain') {
    state.mode = 'none';
  } else {
    const shim = (state.shim ??= createShim());
    try {
      defineGlobal(holder, shim);
    } catch {
      // The page holds the global empty where it cannot be redefined. After `var __REACT_DEVTOOLS_GLOBAL_HOOK__;` in
      // a classic script it can still be assigned, and a plain value misses only a replacement as it happens, which
      // checkHookReplaced still notices at its fixed points. Where it is read-only too, React finds no hook at all.
      try {
        holder[HOOK_KEY] = shim;
      } catch {
        // Read-only.
      }
      if (holder[HOOK_KEY] !== shim) {
        const message =
          "the page's __REACT_DEVTOOLS_GLOBAL_HOOK__ is empty and read-only, so React registers with no hook and its commits cannot be read. Interactions are still reported, without components.";
        unusable(message);
        warnOnce('hook-locked', message);
        return;
      }
    }
    attach(shim, 'shim');
  }
}

/** Stops reading commits, forgets what was read, and puts a chained hook back the way it was. The shim stays: React still holds it. */
export function uninstallHook(): void {
  const hook = state.attached;
  // Looked at before the undo changes the methods: a hook the page turned off since the last look keeps the record
  // it has, or the next install() would take its no-ops for methods React still calls.
  const stillOn = hook && !turnedOff(hook);
  state.detach?.();
  // React calls whatever the hook holds now, the page's own methods put back or another tool's wrapped around the
  // library's, so a later install() over a hook turned off since React registered tells that apart from a no-op.
  if (stillOn) noteCommitMethods(hook);
  state.detach = null;
  state.attached = null;
  state.turnedOffHook = null;
  state.options = null;
  state.mode = 'none';
  state.unsupported = null;
  state.commits = [];
  state.inputs = [];
  state.inTask = null;
  state.derivedTask = null;
  state.walks = 0;
  state.walkTotalMs = 0;
  state.roots = [];
  state.pruneAt = undefined;
  state.listenerWork = new WeakMap();
  state.hearing = false;
  state.awaitingEffects = new WeakMap();
}

function attach(hook: DevtoolsHook, as: 'shim' | 'chained'): void {
  let detach: (() => void) | null = null;
  if (turnedOff(hook)) {
    // React checks both before registering, so it registers with no hook at all. That is as true of a hook
    // assigned over the shim, which an app does when its first import keeps developer tools out, and of the
    // shim itself once the page has turned it off (checkHookReplaced). A react-dom that registered before that
    // goes on calling the hook, and finds the page's methods in place of the library's.
    const message = reactDomOn(hook)
      ? "the page turned its __REACT_DEVTOOLS_GLOBAL_HOOK__ off after react-dom registered with it (isDisabled, or no supportsFiber, with its methods replaced), so React's commits cannot be read. Interactions are still reported, without components."
      : "the page's __REACT_DEVTOOLS_GLOBAL_HOOK__ turns React's developer tools support off (isDisabled, or no supportsFiber), so React registers with no hook and its commits cannot be read. Interactions are still reported, without components.";
    unusable(message);
    warnOnce('hook-disabled', message);
    return;
  }
  try {
    if (as === 'chained') detach = chain(hook);
  } catch {
    // Pages that keep developer tools out of production can freeze the hook, or seal one without
    // onPostCommitFiberRoot, or give a method only a getter, or a setter that drops what it is given.
    const message =
      "the page's __REACT_DEVTOOLS_GLOBAL_HOOK__ is frozen, or has a method that cannot be assigned or added, so it cannot be wrapped and React's commits cannot be read. Interactions are still reported, without components.";
    unusable(message);
    warnOnce('hook-locked', message);
    return;
  }
  state.attached = hook;
  state.mode = as;
  state.unsupported = null;
  state.detach = detach;
  noteCommitMethods(hook);
  for (const renderer of registryOf(hook).values()) admit(renderer);
}

/** Records the two methods React calls on `hook` as the library takes it up or lets it go, for `turnedOff`. */
function noteCommitMethods(hook: DevtoolsHook): void {
  (state.commitMethods ??= new WeakMap()).set(hook, { onCommitFiberRoot: hook.onCommitFiberRoot, onPostCommitFiberRoot: hook.onPostCommitFiberRoot });
}

/** The page has turned the hook off, or locked it or the global, against developer tools: nothing is attached, and the page is unsupported. */
function unusable(message: string): void {
  state.attached = null;
  state.detach = null;
  state.mode = 'unsupported';
  state.unsupported = { kind: 'hook-disabled', message };
}

/** The global, or the hook on it, cannot be read, so no hook the library can reach hears React's commits. */
function globalUnusable(error: unknown): void {
  const message = `the page's __REACT_DEVTOOLS_GLOBAL_HOOK__ cannot be read or replaced (${errorText(error)}), so React's commits cannot be read. Interactions are still reported, without components.`;
  unusable(message);
  warnOnce('hook-disabled', message);
}

function registryOf(hook: DevtoolsHook): Map<number, Renderer> {
  let registry = state.registries.get(hook);
  if (!registry) state.registries.set(hook, (registry = new Map()));
  return registry;
}

/** Records what a renderer handed `inject()` and whether its commits can be read. */
function register(hook: DevtoolsHook, id: number, internals: unknown): Renderer {
  const handed = (internals ?? {}) as { version?: unknown; bundleType?: unknown; rendererPackageName?: unknown };
  const info: RendererInfo = Object.freeze({
    id,
    version: typeof handed.version === 'string' ? handed.version : null,
    bundleType: typeof handed.bundleType === 'number' ? handed.bundleType : null,
    rendererPackageName: typeof handed.rendererPackageName === 'string' ? handed.rendererPackageName : null,
  });
  const version = parseReactVersion(info.version);
  const supported = version !== null && version.major >= OLDEST_REACT_MAJOR && version.major <= NEWEST_REACT_MAJOR;
  const renderer: Renderer = {
    info,
    isReactDom: info.rendererPackageName === 'react-dom',
    experimental: version?.experimental ?? false,
    major: supported ? version.major : 0,
    lanePriority: supported && (version.major > 19 || (version.major === 19 && version.minor >= 1)),
    profileMode: supported ? profileModeBit(version.major) : 0,
    strictMode: supported && info.bundleType === 1 ? strictModeBit(version.major) : 0,
    profiled: null,
    problem: null,
    checked: false,
    devToolsOnly: false,
  };
  if (!supported) renderer.problem = problem('react-version', `react-dom ${info.version ?? 'without a version'} is outside React ${OLDEST_REACT_MAJOR} to ${NEWEST_REACT_MAJOR}`);
  registryOf(hook).set(id, renderer);
  return renderer;
}

function problem(kind: Problem['kind'], reason: string): Problem {
  return { kind, message: `${reason}, so the commits of that react-dom are not read. Interactions are still reported, without its components.` };
}

/** Says once that a react-dom's commits cannot be read. Other renderers are not walked anyway, and are not warned about. */
function admit(renderer: Renderer): void {
  if (renderer.isReactDom && renderer.problem) warnOnce(renderer.problem.message, renderer.problem.message, renderer.problem.kind);
}

/** A React this library does not know is not guessed at: that renderer's commits are not read from here on. */
function stopReading(renderer: Renderer, kind: Problem['kind'], reason: string): void {
  renderer.problem = problem(kind, reason);
  admit(renderer);
}

function onCommit(hook: DevtoolsHook, id: number, root: FiberRoot, priority: number | undefined, didError: boolean | undefined): void {
  const { options } = state;
  if (!options || hook !== state.attached) return;
  // A renderer that registered before install() is unknown here, and its commits are not read.
  const renderer = registryOf(hook).get(id);
  if (!renderer || !renderer.isReactDom || renderer.problem) return;
  // Next.js's dev overlay is not the app, so nothing it commits is walked or tied to an input (see `nextDevToolsRoot`).
  if (nextDevToolsRoot(root)) {
    if (!renderer.checked) renderer.devToolsOnly = true;
    return;
  }
  if (!renderer.checked) checkFirstCommit(renderer, root);
  if (renderer.problem) return;
  // Every commit, walked or not, so that a report that joined none still says which build the page renders with.
  renderer.profiled ||= (root.current.mode & renderer.profileMode) !== 0;
  // Every commit holds a place until React says its passive effects ran, walked or not, so that the
  // report is matched to the commit it belongs to (`onPostCommit`).
  const place: AwaitingEffects = { summary: null, reported: reportsPassiveEffects(root, renderer.major), returnedAt: performance.now() };
  const awaiting = awaitingEffectsOf(root);
  awaiting.push(place);
  if (awaiting.length > MAX_AWAITING_EFFECTS) awaiting.shift();
  placed = place;
  const dispatched = dispatchedInput();
  if (causedByListeners(root, dispatched !== null)) return;
  const now = performance.now();
  // A root's first commit mounts it, or hydrates its server-rendered HTML: the page starting up, not an
  // input's work, unless React ran it inside that input's dispatch (a click that opens a dialog in a root
  // of its own, or React hydrating so that it can handle the click). A Suspense boundary hydrating is the
  // same, and only the walk can find one.
  const firstCommit = root.current.alternate === null || root.current.alternate.child === null;
  const input = dispatched ?? (firstCommit ? null : newestInput());
  // Outside an interaction window this is the whole cost: a few lookups and one subtraction.
  if (!input) return;
  // A commit outside the input's dispatch that something else plainly caused is not the input's, and not
  // a commit it could not account for either: it is left alone, like a commit before any input.
  if (!dispatched && notTheInputs(input, renderer, priority)) return;
  // A commit React ran inside an input's own dispatch is that input's work however long it took to get
  // there: the handler is still on the stack and nothing else can have caused it. Sorting 200,000 rows
  // takes seconds on a throttled machine, and a window measured from the input dropped exactly those
  // commits, leaving the slowest interactions looking as though React had never rendered.
  //
  // A commit outside any dispatch could be anyone's: an effect of this input, or a poll that happened
  // to fire. It joins the newest input while it lands within `inputWindow` of `endedAt`, which is the
  // input itself until React commits inside its dispatch and the end of the last such commit after
  // that. Two failure modes, both of them the window's own:
  //
  // - An unrelated commit landing inside the window is read as the input's follow-up render. That one
  //   the window always had; it is now anchored to the end of the interaction rather than its start.
  // - `endedAt` is the end of the last commit inside the dispatch, not the end of the dispatch. A
  //   handler that runs for two seconds and commits nothing leaves it on the input, so a transition it
  //   starts can land outside the window and be dropped. Dropped is not silent: the time is kept and a
  //   report whose interaction was still being handled then says React rendered something it could not
  //   tie to the interaction. Moving `endedAt` to the end of the dispatch would need a second listener
  //   per event type on the window, in the bubble phase, which is a bigger change than the case is.
  if (!dispatched && now - input.work.endedAt > options.inputWindow) {
    // Kept as times, not as a count: a page with a clock in it commits all day, and only a commit that
    // landed while this interaction's own handlers were running says anything about this interaction.
    // `join.ts` does that filtering; here there is no Event Timing entry to filter against yet.
    const unjoined = input.work.unjoined;
    unjoined.push(now);
    if (unjoined.length > MAX_UNJOINED) unjoined.shift();
    return;
  }
  // In the input's own task the commit is inside that input's Event Timing entry, so INP timed it, even
  // after the paint of an earlier entry of the same interaction. A derived event from a later task is not.
  const inDispatch = dispatched !== null && (dispatched === state.inTask || !inDerivedEvent());
  const t0 = performance.now();
  const hydratedTarget = creditHydration(input);
  // React hydrates the server HTML an input landed on inside the input's own dispatch, to run it, and commits
  // that before any handler runs. The target had no handler to read at dispatch and is read now: by the time
  // the entry comes, the input's own render can have given it another.
  if (hydratedTarget && dispatched) {
    input.handler = handlerOf(fiberFromNode(input.target), input.type, input.key);
    input.hydratedRead = true;
  }
  const walk = walkCommit(root.current, options.walkBudget, now, input, { profileMode: renderer.profileMode, strictMode: renderer.strictMode, priority, didError: didError === true, hydratedTarget });
  const summary: CommitSummary = Object.freeze({ ...walk, walkMs: performance.now() - t0, inDispatch });
  state.walkTotalMs += summary.walkMs;
  state.walks++;
  // Only the dispatch extends the window. Were a joined follow-up to extend it too, one commit every
  // second would keep an interaction's window open for as long as the page lived.
  if (dispatched) {
    input.work.endedAt = performance.now();
    // A derived event from a later task is held to the window from the input's own work, which it does
    // not move, or each one would open the window again for the next.
    if (inDispatch) input.work.ownEndedAt = input.work.endedAt;
  }
  if (summary.hydrated && !dispatched) return;
  if (state.commits.length >= MAX_COMMITS) state.commits.shift();
  state.commits.push(summary);
  place.summary = summary;
  // The walk is done. An error while a report takes the commit in is not the walk's: at most that report's
  // revision is lost, and this react-dom is still read.
  try {
    options.onSummary(summary);
  } catch (error) {
    dropped(error);
  }
}

/**
 * Whether a commit outside any input's dispatch was caused by something other than `input`, the newest
 * input: a resize, scroll or hover being dispatched as React committed (`inAmbientEvent`), the page's
 * width changing since the input (`noteResize`), or, where React says so, continuous-event work. React 18
 * and 19 render a hover's, a scroll's or a wheel's update in a task of its own, where there is no event
 * to read, and React 19.1 and later commit it with user-blocking priority. An input's own updates are
 * immediate, and what its effects, timers and transitions set off is normal or lower, but a touch sets off
 * hover events of its own (pointerover and pointerenter, then the compatibility mouseover and mouseenter),
 * whose updates get that same priority. A finger hovers over nothing, so after a touch the priority says
 * nothing about whose the work is, and neither does it inside the input's own task. Production builds pass
 * no priority, React 18 and 19.0 pass the commit's moment's (normal, in that task) and React 17 its own
 * numbers, so there only the first two apply (docs/known-limits.md).
 */
function notTheInputs(input: InputRecord, renderer: Renderer, priority: number | undefined): boolean {
  if (state.inTask === null && inAmbientEvent()) return true;
  if (state.resizedAt !== undefined && state.resizedAt > input.ts) return true;
  return priority === USER_BLOCKING_PRIORITY && renderer.lanePriority && state.inTask === null && input.pointerType !== 'touch';
}

function awaitingEffectsOf(root: FiberRoot): AwaitingEffects[] {
  let awaiting = state.awaitingEffects.get(root);
  if (!awaiting) state.awaitingEffects.set(root, (awaiting = []));
  return awaiting;
}

/**
 * Puts when `summary`'s passive effects ended on it. A report is built from `state.commits` once the
 * interaction's Event Timing entry arrives, after the paint, so a commit whose effects React ran in the
 * same task has the time by then. The summary already handed to `onSummary` is only read as a render
 * after the paint, whose effects never count.
 */
function effectsRan(summary: CommitSummary, startedAt: number, endedAt: number): void {
  const i = state.commits.lastIndexOf(summary);
  if (i >= 0) state.commits[i] = Object.freeze({ ...summary, effectsStartedAt: startedAt, effectsEndedAt: endedAt });
}

/**
 * The place the hook call now running pushed, until that call returns. Module state rather than page
 * state: the call that pushes it and the wrapper that returns are both this copy's.
 */
let placed: AwaitingEffects | null = null;

/** Every tool on the hook has had the commit; its effects can start from here. */
function hookReturned(): void {
  if (placed) placed.returnedAt = performance.now();
  placed = null;
}

function checkFirstCommit(renderer: Renderer, root: FiberRoot): void {
  renderer.checked = true;
  renderer.devToolsOnly = false;
  const shape = rootShapeProblem(root);
  if (shape) {
    const build = renderer.experimental ? ' (an experimental build, read as React 19)' : '';
    stopReading(renderer, 'fiber-shape', `the fiber tree of react-dom ${renderer.info.version}${build} is not the shape this library reads (${shape})`);
    return;
  }
  // A root's first commit replaces the empty fiber createRoot made. A rendered tree behind the
  // first commit seen here means the root rendered before install(), and those commits were missed.
  if (root.current.alternate?.child) {
    warnOnce('late-install', "install() ran after a React root had already rendered, so its earlier commits were missed. Install with the Vite, Next.js or Astro plugin, or make `import 'react-inp-blame/auto'` the first import of your entry module.");
  }
}

/**
 * Calls the page's report listeners through `hear`, and remembers the React work they cause: a commit
 * during the call, and each lane the call leaves pending on a root. Those commits are the page's own
 * reporting UI, so they are never read as an interaction's render (see `ListenerWork`).
 */
export function hearingReports(hear: () => void): void {
  const roots: [FiberRoot, ListenerWork][] = [];
  state.roots = state.roots.filter((ref) => {
    const root = ref.deref();
    if (root) roots.push([root, listenerWorkOf(root)]);
    return root !== undefined;
  });
  for (const [root, work] of roots) work.seen = root.pendingLanes;
  state.hearing = true;
  try {
    hear();
  } finally {
    state.hearing = false;
    for (const [root, work] of roots) takeNewLanes(root, work);
  }
}

function listenerWorkOf(root: FiberRoot): ListenerWork {
  let work = state.listenerWork.get(root);
  if (!work) {
    state.listenerWork.set(root, (work = { lanes: 0, seen: root.pendingLanes, effectsPending: false }));
    // hearingReports lets go of dropped roots only when a report reaches a listener, so on a page that keeps
    // creating roots and dropping them (a root per toast or map marker) the list would fill with empty refs.
    // They go here too, each time it reaches twice the roots that were alive at the last look.
    if (state.roots.length >= (state.pruneAt ?? MIN_PRUNE_AT)) {
      state.roots = state.roots.filter((ref) => ref.deref() !== undefined);
      state.pruneAt = Math.max(MIN_PRUNE_AT, 2 * state.roots.length);
    }
    state.roots.push(new WeakRef(root));
  }
  return work;
}

/** Counts the lanes set on the root since it was last looked at as the listeners' work. */
function takeNewLanes(root: FiberRoot, work: ListenerWork): void {
  work.lanes |= root.pendingLanes & ~work.seen;
  work.seen = root.pendingLanes;
}

/**
 * Whether this commit is the listeners' work: it ran while they did, or it finished a lane they left
 * pending. What such a commit's render and layout effects schedule is set by the time React calls the
 * hook, so it is theirs too; what its passive effects schedule is taken when React says they ran.
 *
 * Not a commit inside an input's dispatch (`inInput`) that finished such a lane: that is the input's work
 * with theirs rendered along. React 19 renders sync, continuous and default updates in one pass, so a key
 * pressed before React's own task for a listener's update renders that update with the key's. Typing at
 * full speed in the demo, 9 of 23 keystrokes had their commit read as the panel's render and dropped, and
 * their reports said the handler's script took the time. Kept, the commit counts the listener's components
 * beside the input's, and where the input's own render is small they can be what the render blame names.
 * Nor is `effectsPending` set for it, so an update the listener's components make in useEffect reads as
 * the input's later render. Both are the lesser error.
 */
function causedByListeners(root: FiberRoot, inInput: boolean): boolean {
  const work = listenerWorkOf(root);
  const theirs = state.hearing || (!inInput && (work.lanes & ~root.pendingLanes) !== 0);
  // The lanes this commit finished are done with; the ones still pending stay theirs.
  work.lanes &= root.pendingLanes;
  if (theirs) takeNewLanes(root, work);
  else work.seen = root.pendingLanes;
  work.effectsPending = theirs;
  return theirs;
}

/**
 * React 18 and 19, once a commit's passive effects have run. React 17 has no such call, so there an
 * effect of the listeners' render is not recognised, and no commit gets `effectsEndedAt`.
 */
function onPostCommit(hook: DevtoolsHook, id: number, root: FiberRoot): void {
  if (hook !== state.attached) return;
  // The call is the newest waiting commit's that React reports for. React runs a commit's pending
  // effects before it renders again, so no commit can come between one and its call, except those
  // React commits inside that call's passive phase: an update an effect made with `flushSync`, or one
  // a layout effect made, which React renders once the effects are done and before it says so. Those
  // came later, so they sit above it, and their own calls come first. One with no passive work gets no
  // call and is passed over, and one React calls for anyway (a React 19 development build calls for
  // every commit it timed) passes its call down to the commit it was made inside: the effects of that
  // one had ended by then, and the commit's own time is its span, taken out in `join.ts`.
  const awaiting = state.awaitingEffects.get(root);
  if (awaiting && !registryOf(hook).get(id)?.problem) {
    const now = performance.now();
    for (let place = awaiting.pop(); place; place = awaiting.pop()) {
      if (!place.reported) continue;
      if (place.summary) effectsRan(place.summary, place.returnedAt, now);
      break;
    }
  }
  const work = state.listenerWork.get(root);
  if (!work?.effectsPending) return;
  work.effectsPending = false;
  takeNewLanes(root, work);
}

/** React calls the hook inside its commit; nothing here may throw into it. */
function guardedCommit(hook: DevtoolsHook, id: number, root: FiberRoot, priority?: number, didError?: boolean): void {
  placed = null;
  try {
    onCommit(hook, id, root, priority, didError);
  } catch (error) {
    threw(hook, id, error);
  }
}

function guardedPostCommit(hook: DevtoolsHook, id: number, root: FiberRoot): void {
  try {
    onPostCommit(hook, id, root);
  } catch (error) {
    threw(hook, id, error);
  }
}

function threw(hook: DevtoolsHook, id: number, error: unknown): void {
  const renderer = registryOf(hook).get(id);
  if (renderer) stopReading(renderer, 'walk-threw', `reading a commit of react-dom ${renderer.info.version ?? 'without a version'} threw (${errorText(error)})`);
}

/** Wraps a hook someone else installed; returns the undo. Throws for a hook that cannot be wrapped, such as a frozen one. */
function chain(hook: DevtoolsHook): () => void {
  const prevInject = hook.inject;
  const prevCommit = hook.onCommitFiberRoot;
  const hadPostCommit = Object.prototype.hasOwnProperty.call(hook, 'onPostCommitFiberRoot');
  const prevPostCommit = hook.onPostCommitFiberRoot;
  // Until the undo. A method of ours left on the hook after it only passes calls on, also once a later install()
  // wraps the same hook again: that one reads each commit, and this one would read it a second time.
  let live = true;
  const inject = function (this: unknown, ...args: Parameters<DevtoolsHook['inject']>): number {
    const id = prevInject.apply(this, args);
    const renderer = register(hook, id, args[0]);
    if (hook === state.attached) admit(renderer);
    return id;
  };
  const onCommitFiberRoot = function (this: unknown, ...args: Parameters<DevtoolsHook['onCommitFiberRoot']>): void {
    if (live) guardedCommit(hook, ...args);
    try {
      if (typeof prevCommit === 'function') prevCommit.apply(this, args);
    } finally {
      if (live) hookReturned();
    }
  };
  const onPostCommitFiberRoot = function (this: unknown, id: number, root: FiberRoot): void {
    if (live) guardedPostCommit(hook, id, root);
    if (typeof prevPostCommit === 'function') prevPostCommit.call(this, id, root);
  };
  // A page can lock the hook after install(), and dispose() must not throw into it then. A method that cannot be
  // put back stays ours.
  const putBack = (restore: () => void) => {
    try {
      restore();
    } catch {
      // Frozen or sealed since it was wrapped.
    }
  };
  const undo = (failed?: boolean) => {
    live = false;
    // Put the originals back unless another tool has wrapped ours since; then ours stay and pass through. Where
    // wrapping failed, each one that no longer reads as the page's own goes back.
    if (failed ? hook.inject !== prevInject : hook.inject === inject) putBack(() => (hook.inject = prevInject));
    if (failed ? hook.onCommitFiberRoot !== prevCommit : hook.onCommitFiberRoot === onCommitFiberRoot) putBack(() => (hook.onCommitFiberRoot = prevCommit));
    if (failed ? hook.onPostCommitFiberRoot !== prevPostCommit : hook.onPostCommitFiberRoot === onPostCommitFiberRoot) {
      // Put back before it is removed, so that on a hook sealed since, which keeps the property, React calls only
      // what it did before (nothing, or a method the hook inherits) and a later install() does not wrap ours.
      putBack(() => (hook.onPostCommitFiberRoot = prevPostCommit));
      if (!hadPostCommit) putBack(() => delete hook.onPostCommitFiberRoot);
    }
  };
  try {
    if (typeof prevInject === 'function') hook.inject = inject;
    hook.onCommitFiberRoot = onCommitFiberRoot;
    hook.onPostCommitFiberRoot = onPostCommitFiberRoot;
    // A setter that drops what it is given throws nothing, and React's commits would never come here. So each
    // method is read back, and one that still reads as the page's own is put back like one that threw. One that
    // reads as something else took it all the same: a setter that wraps what it is given passes React's calls on.
    if ((typeof prevInject === 'function' && hook.inject === prevInject) || hook.onCommitFiberRoot === prevCommit || hook.onPostCommitFiberRoot === prevPostCommit) {
      throw new TypeError();
    }
    // Renderers that registered before install(): React DevTools' hook kept what they handed it. Read inside the
    // guard, so a hook whose renderers cannot be read is put back as it was too.
    if (hook.renderers instanceof Map) {
      const registry = registryOf(hook);
      for (const [id, internals] of hook.renderers) if (!registry.has(id)) register(hook, id, internals);
    }
  } catch (error) {
    // A method that cannot be assigned, or renderers that cannot be read: the ones already wrapped are put back,
    // so the page's hook is as it was.
    undo(true);
    throw error;
  }
  return undo;
}

/**
 * The least React needs to register and report commits, and the call after a commit's passive effects.
 * React checks for every other hook method before calling it (17.0.2, 18.3.1 and 19.3.0 alike), and
 * there is deliberately no `checkDCE`: react-dom reads that as React DevTools being present.
 */
function createShim(): DevtoolsHook {
  let nextId = 0;
  const renderers = new Map<number, unknown>();
  const hook: DevtoolsHook = {
    renderers,
    supportsFiber: true,
    inject(internals) {
      const id = ++nextId;
      // Kept the way React DevTools keeps them, for tools that chain on later (Fast Refresh reads them).
      renderers.set(id, internals);
      const renderer = register(hook, id, internals);
      if (hook === state.attached) admit(renderer);
      return id;
    },
    onCommitFiberRoot(id, root, priority, didError) {
      guardedCommit(hook, id, root, priority, didError);
      hookReturned();
    },
    onPostCommitFiberRoot(id, root) {
      guardedPostCommit(hook, id, root);
    },
    reactInpBlame: true,
  };
  return hook;
}

/**
 * Makes the shim the global through an accessor rather than a plain value, so that another tool
 * assigning its own hook later is noticed instead of silently winning or losing. React DevTools
 * never assigns: its installHook returns as soon as `window` has the property, without a read or a
 * write the accessor could see, so a shim that got there first locks it out unnoticed. The setter runs
 * inside the other tool's assignment, so nothing it throws may reach that tool.
 */
function defineGlobal(holder: HookHolder, hook: DevtoolsHook): void {
  let current: unknown = hook;
  const follow = guarded(replaced);
  Object.defineProperty(holder, HOOK_KEY, {
    configurable: true,
    enumerable: false,
    get: () => current,
    set(next: unknown) {
      current = next;
      if (next !== hook) follow(hook, next);
    },
  });
}

function replaced(hook: DevtoolsHook, next: unknown): void {
  if (hook !== state.attached) return;
  if (registryOf(hook).size) {
    // React holds on to the hook it registered with, so it keeps reporting to the shim.
    state.devtoolsLockedOut = true;
    warnOnce('locked-out', "__REACT_DEVTOOLS_GLOBAL_HOOK__ was replaced after React registered with react-inp-blame's hook, so the tool that replaced it will not see this React. Load that tool before react-inp-blame, or install with hook: 'chain'.");
  } else if (next && typeof next === 'object') {
    // Nothing has registered yet, so React will register with the replacement, unless it turns React's support
    // off: follow it, the way install() takes a hook it finds.
    attach(next as DevtoolsHook, 'chained');
  }
}
